import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  ActionIcon,
  Menu,
  Modal,
  TextInput,
  SegmentedControl,
  Switch,
  Stack,
  LoadingOverlay,
  Image,
  ScrollArea,
  Divider,
  Avatar,
  ThemeIcon,
  Center,
  Card,
  Tooltip,
} from "@mantine/core";
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconPhoto,
  IconCheck,
  IconLink,
  IconArrowRight,
  IconArrowLeft,
  IconX,
} from "@tabler/icons-react";
import { useNotification } from "../../adminProjectNotifications/popupnotifications";
import { ImageUploadComponent } from "../adminProjectManagement";
import { API_BASE_URL as ROOT_API_BASE_URL } from "../../../config/baseUrls";

// --- AUTH & API ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

const API_BASE_URL = `${ROOT_API_BASE_URL}/admin`;

// --- COMPONENT: LIVE MOBILE PREVIEW ---
const LiveMobilePreview = ({ bannerData }) => {
  return (
    <Center h="100%">
      <Box
        w={280}
        h={560}
        bg="black"
        style={{
          borderRadius: "40px",
          border: "10px solid #1f1f1f",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 20px 50px -10px rgba(0,0,0,0.3)",
        }}
      >
        {/* Phone UI: Status Bar */}
        <Group
          justify="space-between"
          px="md"
          pt="sm"
          className="absolute top-0 w-full z-20"
        >
          <Text size="10px" fw={700} c="white">
            9:41
          </Text>
          <Group gap={2}>
            <Box
              w={12}
              h={12}
              bg="white"
              style={{ borderRadius: "50%" }}
              opacity={0.8}
            />
            <Box
              w={12}
              h={6}
              bg="white"
              style={{ borderRadius: 2 }}
              opacity={0.8}
            />
          </Group>
        </Group>

        {/* App Mockup: Ideas Feed */}
        <Box
          bg="#f8f9fa"
          w="100%"
          h="100%"
          pt={40}
          px="md"
          style={{ overflowY: "auto" }}
        >
          <Text size="xl" fw={800} mb="sm" mt="md">
            Ideas
          </Text>

          {/* THE BANNER PREVIEW */}
          <Paper
            radius="lg"
            className="overflow-hidden relative shadow-md"
            style={{ aspectRatio: "16/9", width: "100%" }}
            bg="white"
          >
            {bannerData.imageUrl ? (
              <>
                <Image
                  src={bannerData.imageUrl}
                  h="100%"
                  w="100%"
                  fit="cover"
                  alt="Preview"
                />
                <Box className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              </>
            ) : (
              <Center h="100%" bg="gray.1">
                <IconPhoto size={32} className="text-gray-300" />
              </Center>
            )}

            {/* Text Overlay */}
            <Box className="absolute bottom-3 left-3 right-3 text-white">
              <Text size="sm" fw={700} lh={1.2} mb={4}>
                {bannerData.bannerText || "Your Banner Text Here"}
              </Text>
              {bannerData.navigateTo !== "none" && (
                <Group gap={4} opacity={0.9}>
                  <Text size="xs" fw={600} tt="uppercase">
                    {bannerData.navigateTo === "Voting"
                      ? "View Project"
                      : "Shop Now"}
                  </Text>
                  <IconArrowRight size={10} />
                </Group>
              )}
            </Box>
          </Paper>

          {/* Mock List Items Below */}
          <Stack mt="xl" gap="md">
            {[1, 2].map((i) => (
              <Paper key={i} p="sm" radius="md" withBorder>
                <Group align="start">
                  <Box w={60} h={60} bg="gray.1" style={{ borderRadius: 8 }} />
                  <Stack gap={4} style={{ flex: 1 }}>
                    <Box
                      w="80%"
                      h={10}
                      bg="gray.3"
                      style={{ borderRadius: 4 }}
                    />
                    <Box
                      w="40%"
                      h={8}
                      bg="gray.2"
                      style={{ borderRadius: 4 }}
                    />
                  </Stack>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Box>
      </Box>
    </Center>
  );
};

// --- COMPONENT: BANNER FORM MODAL ---
const BannerFormModal = ({ isOpen, onClose, banner, onSuccess }) => {
  const [loading, setLoading] = useState(false);

  // FORM STATE
  const [formData, setFormData] = useState({
    bannerTitle: "",
    bannerText: "",
    imageUrl: "",
    isActive: true,
    navigateTo: "none",
    selectedItem: null,
  });

  // ITEM SELECTION STATE
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsPagination, setItemsPagination] = useState({
    page: 1,
    hasNext: false,
  });

  const { showSuccess, showError } = useNotification();

  // RESET FORM
  useEffect(() => {
    if (isOpen) {
      if (banner) {
        let navigateTo = "none";
        let selectedItem = banner.metaData || null;
        if (banner.navigateTo) navigateTo = banner.navigateTo;

        setFormData({
          bannerTitle: banner.bannerTitle || "",
          bannerText: banner.bannerText || "",
          imageUrl: banner.imageUrl || "",
          isActive: banner.isActive ?? true,
          navigateTo,
          selectedItem,
        });
      } else {
        setFormData({
          bannerTitle: "",
          bannerText: "",
          imageUrl: "",
          isActive: true,
          navigateTo: "none",
          selectedItem: null,
        });
        setItems([]);
      }
    }
  }, [isOpen, banner]);

  // FETCH ITEMS
  useEffect(() => {
    if (formData.navigateTo !== "none" && isOpen) fetchItems(1);
    else {
      setItems([]);
    }
  }, [formData.navigateTo]);

  const fetchItems = async (page = 1) => {
    setItemsLoading(true);
    try {
      const endpoint =
        formData.navigateTo === "Voting"
          ? `${API_BASE_URL}/admin/projects/pending?status=ACTIVE&page=${page}&limit=20`
          : `${API_BASE_URL}/admin/products?status=AVAILABLE&page=${page}&limit=20`;

      const res = await fetch(endpoint, { headers: getAuthHeaders() });
      const data = await res.json();

      if (data.success) {
        const newItems =
          formData.navigateTo === "Voting"
            ? data.data.projects
            : data.data.products;
        setItems((prev) => (page === 1 ? newItems : [...prev, ...newItems]));
        setItemsPagination(data.data.pagination);
      }
    } catch {
      showError("Error", "Could not load items");
    } finally {
      setItemsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.bannerTitle.trim())
      return showError("Missing Title", "Please provide an internal title");
    if (!formData.imageUrl.trim())
      return showError("Missing Image", "Please upload a banner image");

    setLoading(true);
    try {
      const endpoint = banner
        ? `${API_BASE_URL}/ideas-banner/${banner.id}`
        : `${API_BASE_URL}/create-ideas-banner`;

      const payload = {
        bannerTitle: formData.bannerTitle,
        bannerText: formData.bannerText,
        imageUrl: formData.imageUrl,
        isActive: formData.isActive,
        navigateTo: formData.navigateTo === "none" ? null : formData.navigateTo,
        metaData: formData.navigateTo === "none" ? null : formData.selectedItem,
      };

      const res = await fetch(endpoint, {
        method: banner ? "PUT" : "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.success) {
        showSuccess("Success", banner ? "Banner updated" : "Banner created");
        onSuccess();
        onClose();
      } else {
        showError("Error", data.error || "Failed to save");
      }
    } catch {
      showError("Error", "Network error");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (item) => {
    const isProject = formData.navigateTo === "Voting";
    const newItem = {
      [isProject ? "projectId" : "productId"]: item.id,
      isCosplayEvent: item.isCosplayEvent,
      isMerch: isProject
        ? item.projectType === "NEW_PRODUCT"
        : item.productType === "NEW_PRODUCT",
      _tempTitle: item.title, // Helper for UI display
    };
    setFormData({ ...formData, selectedItem: newItem });
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Text fw={700} size="lg">
          {banner ? "Edit Idea Banner" : "New Idea Banner"}
        </Text>
      }
      size="85%"
      padding={0}
      radius="lg"
      styles={{ body: { height: "650px", display: "flex" } }}
    >
      <Grid w="100%" h="100%" gutter={0}>
        {/* LEFT: FORM */}
        <Grid.Col
          span={7}
          p="xl"
          style={{ borderRight: "1px solid #eee", overflowY: "auto" }}
        >
          <Stack gap="lg">
            <LoadingOverlay visible={loading} />

            <TextInput
              label="Internal Title"
              description="Used for admin reference only"
              placeholder="e.g. June Cosplay Promo"
              required
              value={formData.bannerTitle}
              onChange={(e) =>
                setFormData({ ...formData, bannerTitle: e.target.value })
              }
            />

            <TextInput
              label="Display Text"
              description="Text overlay shown to users"
              placeholder="e.g. Discover New Cosplays"
              value={formData.bannerText}
              onChange={(e) =>
                setFormData({ ...formData, bannerText: e.target.value })
              }
            />

            <Box>
              <Text size="sm" fw={500} mb={4}>
                Banner Image (16:9 Ratio)
              </Text>
              <ImageUploadComponent
                onUpload={(url) => setFormData({ ...formData, imageUrl: url })}
                existingUrl={formData.imageUrl}
              />
            </Box>

            <Divider label="Interaction" labelPosition="left" />

            <Stack gap="xs">
              <Text size="sm" fw={500}>
                On Tap Action
              </Text>
              <SegmentedControl
                fullWidth
                value={formData.navigateTo}
                onChange={(v) =>
                  setFormData({
                    ...formData,
                    navigateTo: v,
                    selectedItem: null,
                  })
                }
                data={[
                  { label: "No Action", value: "none" },
                  { label: "Open Project", value: "Voting" },
                  { label: "Open Store", value: "Purchase" },
                ]}
              />
            </Stack>

            {/* DYNAMIC ITEM SELECTOR */}
            {formData.navigateTo !== "none" && (
              <Box className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {formData.selectedItem ? (
                  <Group
                    justify="space-between"
                    bg="white"
                    p="sm"
                    className="border rounded-md shadow-sm"
                  >
                    <Group>
                      <ThemeIcon
                        color={
                          formData.navigateTo === "Voting" ? "blue" : "purple"
                        }
                        variant="light"
                      >
                        <IconCheck size={16} />
                      </ThemeIcon>
                      <Box>
                        <Text size="sm" fw={600}>
                          Linked Successfully
                        </Text>
                        <Text
                          size="xs"
                          c="dimmed"
                          style={{ fontFamily: "monospace" }}
                        >
                          ID:{" "}
                          {formData.selectedItem.projectId ||
                            formData.selectedItem.productId}
                        </Text>
                      </Box>
                    </Group>
                    <ActionIcon
                      color="red"
                      variant="subtle"
                      onClick={() =>
                        setFormData({ ...formData, selectedItem: null })
                      }
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Group>
                ) : (
                  <Stack gap="xs">
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                      Select Item
                    </Text>
                    <ScrollArea
                      h={150}
                      className="bg-white rounded border border-gray-200"
                    >
                      <Stack gap={0}>
                        {items.map((item) => (
                          <Group
                            key={item.id}
                            className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-0"
                            onClick={() => handleSelectItem(item)}
                            wrap="nowrap"
                          >
                            <Avatar
                              src={item.primaryImage}
                              size="sm"
                              radius="sm"
                            />
                            <Text size="sm" lineClamp={1} style={{ flex: 1 }}>
                              {item.title}
                            </Text>
                            <Badge size="xs" variant="outline">
                              {item.projectType || item.productType}
                            </Badge>
                          </Group>
                        ))}
                        {itemsPagination.hasNext && (
                          <Button
                            variant="subtle"
                            fullWidth
                            size="xs"
                            onClick={() => fetchItems(itemsPagination.page + 1)}
                          >
                            Load More
                          </Button>
                        )}
                      </Stack>
                    </ScrollArea>
                  </Stack>
                )}
              </Box>
            )}

            <Group justify="space-between" mt="auto" pt="lg">
              <Switch
                label="Set Banner Active"
                size="md"
                checked={formData.isActive}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    isActive: e.currentTarget.checked,
                  })
                }
              />
              <Group>
                <Button variant="subtle" color="gray" onClick={onClose}>
                  Discard
                </Button>
                <Button
                  onClick={handleSubmit}
                  color="blue"
                  leftSection={<IconCheck size={16} />}
                >
                  Save Changes
                </Button>
              </Group>
            </Group>
          </Stack>
        </Grid.Col>

        {/* RIGHT: LIVE PREVIEW */}
        <Grid.Col
          span={5}
          bg="gray.0"
          p="xl"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Text fw={700} c="dimmed" tt="uppercase" size="xs" lts={2} mb="lg">
            Live Preview
          </Text>
          <LiveMobilePreview bannerData={formData} />
        </Grid.Col>
      </Grid>
    </Modal>
  );
};

// --- MAIN PAGE ---
export default function IdeasBannerManagement() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const { showSuccess, showError } = useNotification();

  const fetchBanners = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/ideaBanners`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.status === "success") {
        setBanners(data.banners.map((b) => ({ ...b, id: b.ideaBannerId })));
      }
    } catch {
      showError("Error", "Failed to fetch banners");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleToggle = async (banner) => {
    const oldBanners = [...banners];
    setBanners(
      banners.map((b) =>
        b.id === banner.id ? { ...b, isActive: !b.isActive } : b,
      ),
    );
    try {
      const res = await fetch(
        `${API_BASE_URL}/ideas-banner/${banner.id}/toggle`,
        { method: "PUT", headers: getAuthHeaders() },
      );
      const data = await res.json();
      if (data.success) showSuccess("Updated", "Banner status updated");
      else throw new Error();
    } catch {
      setBanners(oldBanners);
      showError("Error", "Update failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      const res = await fetch(`${API_BASE_URL}/ideas-banner/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        showSuccess("Deleted", "Banner removed");
        fetchBanners();
      }
    } catch {
      showError("Error", "Delete failed");
    }
  };

  const moveBanner = async (index, direction) => {
    const newBanners = [...banners];
    const targetIndex = index + direction;

    if (targetIndex < 0 || targetIndex >= newBanners.length) return;

    [newBanners[index], newBanners[targetIndex]] = [
      newBanners[targetIndex],
      newBanners[index],
    ];
    setBanners(newBanners);

    try {
      const orderedIds = newBanners.map((b) => b.id);

      await fetch(`${API_BASE_URL}/ideas-banner/reorder`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ orderedIds }),
      });
    } catch (error) {
      showError("Error", "Failed to save new order");
      fetchBanners();
    }
  };

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Ideas Banners
          </Text>
          <Text size="sm" c="dimmed">
            Manage content slides for the Ideas tab
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={18} />}
          className="btn-ota-primary"
          onClick={() => {
            setSelectedBanner(null);
            setShowModal(true);
          }}
        >
          New Banner
        </Button>
      </Group>

      {loading ? (
        <Center h={200}>
          <Stack align="center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <Text c="dimmed">Loading...</Text>
          </Stack>
        </Center>
      ) : banners.length === 0 ? (
        <Paper p="xl" withBorder radius="md" ta="center" bg="gray.0">
          <ThemeIcon size={60} radius="xl" color="gray" variant="light" mb="md">
            <IconPhoto size={30} />
          </ThemeIcon>
          <Text fw={600} size="lg">
            No Ideas Banners
          </Text>
          <Button
            mt="md"
            variant="white"
            onClick={() => {
              setSelectedBanner(null);
              setShowModal(true);
            }}
          >
            Create First Banner
          </Button>
        </Paper>
      ) : (
        <Grid>
          {banners.map(
            (
              banner,
              index, // Note: Added 'index' here
            ) => (
              <Grid.Col key={banner.id} span={{ base: 12, md: 6, lg: 4 }}>
                <Card
                  withBorder
                  radius="md"
                  padding="0"
                  className="overflow-hidden shadow-sm hover:shadow-md transition-shadow h-full flex flex-col"
                >
                  {/* Visual Header */}
                  <Box
                    className="relative bg-gray-100"
                    style={{ aspectRatio: "16/9" }}
                  >
                    <Image
                      src={banner.imageUrl}
                      h="100%"
                      w="100%"
                      fit="cover"
                    />

                    {/* TOP ROW: REORDER CONTROLS + MENU */}
                    <Group
                      className="absolute top-2 left-2 right-2 z-10"
                      justify="space-between"
                    >
                      {/* NEW: REORDER BUTTONS */}
                      <Group
                        gap={4}
                        bg="rgba(0,0,0,0.5)"
                        p={4}
                        style={{ borderRadius: 8 }}
                      >
                        <ActionIcon
                          variant="transparent"
                          c="white"
                          size="sm"
                          disabled={index === 0}
                          onClick={() => moveBanner(index, -1)}
                        >
                          <IconArrowLeft size={16} />
                        </ActionIcon>
                        <Text c="white" size="xs" fw={700}>
                          {index + 1}
                        </Text>
                        <ActionIcon
                          variant="transparent"
                          c="white"
                          size="sm"
                          disabled={index === banners.length - 1}
                          onClick={() => moveBanner(index, 1)}
                        >
                          <IconArrowRight size={16} />
                        </ActionIcon>
                      </Group>

                      {/* EXISTING MENU */}
                      <Menu position="bottom-end" shadow="md">
                        <Menu.Target>
                          <ActionIcon
                            variant="white"
                            radius="xl"
                            size="lg"
                            className="shadow-md opacity-90 hover:opacity-100"
                          >
                            <IconDotsVertical size={18} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconEdit size={14} />}
                            onClick={() => {
                              setSelectedBanner(banner);
                              setShowModal(true);
                            }}
                          >
                            Edit
                          </Menu.Item>
                          <Menu.Divider />
                          <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={() => handleDelete(banner.id)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>

                    {/* Banner Text Overlay */}
                    <Box className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                      <Text
                        c="white"
                        fw={700}
                        size="sm"
                        lineClamp={2}
                        style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
                      >
                        {banner.bannerText || ""}
                      </Text>
                    </Box>
                  </Box>

                  {/* Content Body (Unchanged) */}
                  <Stack p="md" gap="xs" style={{ flex: 1 }}>
                    {/* ... keep your existing body code here ... */}
                    {/* ... e.g. Title, Date, Toggle Switch ... */}
                    <Group justify="space-between" align="start">
                      <Box style={{ flex: 1 }}>
                        <Text fw={600} size="sm" lineClamp={1}>
                          {banner.bannerTitle}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {new Date(banner.createdAt).toLocaleDateString()}
                        </Text>
                      </Box>
                      <Switch
                        checked={banner.isActive}
                        onChange={() => handleToggle(banner)}
                        color="green"
                        size="sm"
                        thumbIcon={
                          banner.isActive ? (
                            <IconCheck size={10} color="green" />
                          ) : null
                        }
                      />
                    </Group>

                    <Box mt="auto">
                      {banner.navigateTo && banner.navigateTo !== "none" ? (
                        <Badge
                          variant="light"
                          color="blue"
                          fullWidth
                          leftSection={<IconLink size={12} />}
                          radius="sm"
                        >
                          Opens {banner.navigateTo}
                        </Badge>
                      ) : (
                        <Badge
                          variant="light"
                          color="gray"
                          fullWidth
                          radius="sm"
                        >
                          Static Banner
                        </Badge>
                      )}
                    </Box>
                  </Stack>
                </Card>
              </Grid.Col>
            ),
          )}
        </Grid>
      )}

      {/* MODAL INJECTION */}
      <BannerFormModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        banner={selectedBanner}
        onSuccess={fetchBanners}
      />
    </Box>
  );
}
